<template lang="pug">
div
  nav.order-nav
    a.order-nav-dot(
      v-for="(step, idx) in steps"
      href="#"
      role="button"
      :class="{ 'is-active': currentStep === idx }"
      @click.prevent="goToStep(idx)"
    )
  .grid.is-ultrawide-6.is-widescreen-7.is-desktop-8.u-mh-md.is-center
    .column
      ValidationObserver(ref="obs")
        transition-group(name="order")
          OrderStep(
            v-for="(step, n) in steps"
            v-if="n === currentStep"
            :tip="step.type === 'textarea' ? 'linebreak' : 'pressEnter'"
            :key="step.id"
            @next="nextStep"
            :controls="!isLastStep"
            :isFirst="isFirstStep"
          )
            component(:is="step.component" v-bind="step.props" v-on="step.listeners || {}" :ref="step.name")
          div.u-mb-ts(v-show="isLastStep" key="recaptcha")
            recaptcha(@error="onError" @success="onSuccess" @expired="onExpired")
          button.button-link.is-medium(v-if="isLastStep" v-load="isSubmitting" key="action" @click="nextStep") {{ $t('ordering.start') }}
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { ValidationObserver, ValidationProvider } from 'vee-validate/dist/vee-validate.minimal';
import Dropzone from '~/components/Dropzone';
import OrderStep from '~/components/OrderStep';
import { load } from '~/directives';
import TextInput from '~/components/inputs/TextInput';
import SelectInput from '~/components/inputs/SelectInput';
import PhoneInput from '~/components/inputs/PhoneInput';
import { CreateOrder } from '~/graphql/Order.gql';

export default {
  scrollToTop: true,
  meta: {
    theme: 'order'
  },

  head() {
    const $t = this.$t.bind(this);

    return {
      title: $t('pageTitle'),
      meta: [
        {
          hid: 'og:title',
          name: 'og:title',
          property: 'og:title',
          content: $t('pageTitle')
        },
        {
          hid: 'description',
          name: 'description',
          content: $t('pageDesc')
        },
        {
          hid: 'og:description',
          name: 'og:description',
          property: 'og:description',
          content: $t('pageDesc')
        }
      ]
    };
  },

  components: {
    Dropzone,
    TextInput,
    SelectInput,
    PhoneInput,
    OrderStep,
    ValidationProvider,
    ValidationObserver
  },
  directives: {
    load
  },
  data() {
    return {
      recaptchaValid: false,
      rawSteps: [
        {
          id: 0,
          name: 'firstName',
          component: 'TextInput',
          labelKey: 'ordering.wName',
          nameKey: 'forms.fname',
          props: {
            type: 'text',
            value: '',
            rules: 'required'
          },
          isDone: false,
          skipIfAuth: true
        },
        {
          id: 1,
          name: 'lastName',
          component: 'TextInput',
          labelKey: 'ordering.lName',
          nameKey: 'forms.fname',
          props: {
            type: 'text',
            value: '',
            rules: 'required'
          },
          isDone: false,
          skipIfAuth: true
        },
        {
          id: 2,
          name: 'projectName',
          component: 'TextInput',
          labelKey: 'ordering.greeting',
          nameKey: 'ordering.projectName',
          props: {
            type: 'text',

            value: '',
            rules: 'required'
          },
          skipIfAuth: true,
          isDone: false
        },
        {
          id: 3,
          name: 'projectName',
          component: 'TextInput',
          labelKey: 'ordering.greetingRecurring',
          nameKey: 'ordering.projectName',
          props: {
            type: 'text',
            value: '',
            rules: 'required'
          },
          isDone: false
        },
        {
          id: 4,
          name: 'projectDetails',
          component: 'TextInput',
          labelKey: 'ordering.projectMore',
          nameKey: 'ordering.projectDetails',
          props: {
            type: 'textarea',
            value: '',
            rules: 'required'
          },
          isDone: false
        },
        {
          id: 5,
          name: 'help',
          component: 'TextInput',
          labelKey: 'ordering.help',
          nameKey: 'ordering.helpAs',
          props: {
            type: 'textarea',
            value: '',
            rules: 'required'
          },
          isDone: false
        },
        {
          id: 7,
          name: 'email',
          component: 'TextInput',
          labelKey: 'ordering.email',
          nameKey: 'forms.email',
          props: {
            type: 'email',
            value: '',
            rules: 'required|email'
          },
          isDone: false,
          skipIfAuth: true
        },
        {
          id: 8,
          name: 'phone',
          component: 'PhoneInput',
          labelKey: 'ordering.phone',
          nameKey: 'ordering.phoneAs',
          props: {
            type: 'text',
            value: '',
            rules: 'required'
          },
          isDone: false,
          skipIfAuth: true
        }
      ],
      currentStep: 0,
      isSubmitting: false
    };
  },
  computed: {
    ...mapGetters({
      isLoggedIn: 'auth/isLoggedIn',
      user: 'auth/user'
    }),
    isFirstStep() {
      return this.currentStep === 0;
    },
    isLastStep() {
      return this.currentStep === this.steps.length - 1;
    },
    langParams() {
      const name = this.isLoggedIn ? this.user.firstName : this.rawSteps.find(s => s.name === 'firstName').props.value;
      let project = this.rawSteps.find(s => s.name === 'projectName' && s.props.value);
      project = !project ? '' : project.props.value;

      return { name, project };
    },
    steps() {
      return this.rawSteps.reduce((arr, step) => {
        if (this.isLoggedIn && step.skipIfAuth) {
          return arr;
        }

        if (arr.find(s => s.name === step.name)) {
          return arr;
        }

        step.listeners = {
          input: e => {
            step.props.value = e;
          }
        };
        if (step.component === 'TextInput') {
          step.props.autofocus = true;
          step.listeners.keydown = e => {
            if (e.keyCode !== 13 || e.shiftKey) return;

            e.preventDefault();
            this.nextStep();
          };
        }

        step.props.name = this.$t(step.nameKey, this.langParams);
        step.props.label = this.$t(step.labelKey, this.langParams);

        arr.push(step);

        return arr;
      }, []);
    }
  },
  methods: {
    ...mapActions({
      success: 'alert/success',
      error: 'alert/error',
      identifyUser: 'auth/fetch'
    }),
    async nextStep() {
      // don't move on unless all is valid.
      const isValid = await this.$refs.obs.validate();
      if (!isValid) {
        return;
      }

      // last step
      if (this.isLastStep) {
        return this.submit();
      }

      this.goToStep(this.currentStep + 1, true);
    },
    goToStep(id, skip = false) {
      if (skip) {
        // tag it as done, so the user can go back/to the step.
        this.steps[this.currentStep].isDone = true;
        this.currentStep = id;
      } else {
        this.currentStep = this.steps[id].isDone ? id : this.currentStep;
      }

      if (this.steps[this.currentStep].component === 'SelectInput') {
        const listener = e => {
          try {
            if (e.keyCode !== 13 || e.shiftKey) return;

            e.preventDefault();
            this.nextStep();
            window.removeEventListener('keydown', listener);
          } catch (err) {
            console.error(err);
          }
        };

        setTimeout(() => {
          window.addEventListener('keydown', listener);
        }, 1000);
      }
    },
    extractInput() {
      return this.steps.reduce((prev, step) => {
        prev[step.name] = step.props.value;

        return prev;
      }, {});
    },
    async submit() {
      try {
        if (!this.recaptchaValid) {
          this.error(this.$t('forms.recaptchaRequired'));
          return;
        }
        this.isSubmitting = true;

        const { data } = await this.$api.mutate({
          mutation: CreateOrder,
          variables: {
            input: {
              ...this.extractInput(),
              country: this.$refs.phone[0].country.name
            }
          }
        });

        await this.identifyUser(data.orderProject.authToken);
        this.success(this.$t('ordering.success'));
        this.$router.localePush('/cc/projects');
        await this.$recaptcha.reset();
        this.recaptchaValid = false;
      } catch (err) {
        this.error(err.message);
      }
    },
    onError() {
      this.recaptchaValid = false;
    },
    onSuccess() {
      this.recaptchaValid = true;
    },
    onExpired() {
      this.recaptchaValid = false;
    }
  },
  async mounted() {
    try {
      await this.$recaptcha.init();
    } catch (e) {
      console.error(e);
    }
  }
};
</script>

<style lang="stylus">
.order
  position: relative

  &-step
    display: flex
    align-items: flex-center
    justify-content: center
    flex-direction: column

    .form
      width: 100%

      &-label
        font-size: $size-large
        margin-bottom: unitRes(16, 32)
        max-width: 70%

        +mq($until: 'mobile')
          max-width: 100%

  &-next
    display: flex
    align-items: center

    p
      font-size: 14px
      margin: 0 20px

  &-nav
    position: fixed
    top: 50%
    right: 8vw
    transform: translate3d(0, -50%, 0)
    display: flex
    flex-direction: column
    align-items: flex-end
    z-index: 33

    &-dot
      display: block
      width: unitRes(8, 16)
      height: unitRes(4, 8)
      margin: unitRes(5, 10) 0
      background-color: $clBlack
      cursor: pointer

      &:hover
        background-color: $clSecondary

      &.is-active
        background-color: $clPrimary
        width: unitRes(20, 40)

    +mq($until: 'desktop')
      display: none

body.is-order
  .footer
    display: none

  height: 100vh
  width: 100%

  .app
    min-height: 100vh

  .app-body
    position: absolute
    top: 0
    right: 0
    left: 0
    bottom: 0
    display: flex
    justify-content: center
    align-items: center
    padding-right: $outerMargin
    padding-left: $outerMargin

    +mq($until: 'tablet')
      padding-right: $outerMargin-sm
      padding-left: $outerMargin-sm

    >div
      width: 100%

  &.is-last
    .app-body
      display: block

      .order-step
        margin-bottom: unitRes(40, 80)

.order-leave-active, .order-enter-active
  transition: all 0.5s

.order-enter
  opacity: 0
  transform: translateY(100px)

.order-leave-active
  opacity: 0
  height: 0px
  transform: translateY(-200px)

.order-tip
  +mq($until: 'mobile')
    display: none
</style>

<i18n>
{
  "en": {
    "pageTitle":"Ordering",
    "pageDesc":"Tell us about your project and needs. Use the form to submit your service request and get in touch with Baianat.",
    "ordering": {
      "skip": "Skip",
      "order": "Order now",
      "wName": "Hello, What's your first name?",
      "lName": "Hello, What's your last name?",
      "projectName": "Project name",
      "greeting": "Nice to meet you, {name}, what is the name of your project?",
      "greetingRecurring": "Hello again {name}, what is the name of your project?",
      "email": "Which email we can reach you through?",
      "projectMore": "Can you tell us more about {project}?",
      "projectDetails": "Project details",
      "help": "Perfect! So, How we can help you?",
      "helpAs": "Help",
      "budget": "Which budget range would be more suitable for you?",
      "budgetAs": "budget",
      "phone": "Which phone number we can reach you through?",
      "phoneAs": "Phone",
      "start": "Let’s start the conversation!",
      "success": "Your order was submitted successfully! We will get back to you shortly."
    }
  },
  "ar": {
    "pageTitle":"طلب الخدمة",
    "pageDesc":"أخبرنا كل التفاصيل الممكنة عن مشروعك التجاري سواء القادم أو الذي تعمل عليه حاليًا لنتمكن من مساعدتك بكل الوسائل الممكنة",
    "ordering": {
      "skip": "تخطي",
      "order": "اطلب الان",
      "wName": "مرحبًا، ما اسمك الأول؟",
      "lName": " ؟ ما اسمك الأخير",
      "projectName": "اسم المشروع",
      "greeting": "سعداء بمعرفتك، {name} ، ما اسم مشروعك؟ ",
      "greetingRecurring": "مرحبًا مجددًا،{name} ، ما اسم مشروعك؟ ",
      "email": "ما عنوان البريد الإلكتروني الذي يمكننا الوصول إليك من خلاله؟ ",
      "projectMore": "هل يمكنك إخبارنا بالمزيد عن {project}   ؟",
      "projectDetails": "تفاصيل المشروع",
      "help": "رائع! لكن كيف يمكننا مساعدتك؟",
      "helpAs": "مساعدة",
      "budget": "ما هي حدود الميزانية المناسب أكثر لك؟ ",
      "budgetAs": "الميزانية",
      "phone": "أي رقم هاتف يمكننا التواصل معك من خلاله؟",
      "phoneAs": "الهاتف",
      "start": "دعنا نبدأ المحادثة !",
      "success": "لقد قمنا باستلام طلبك بنجاح. سنقوم بالتواصل معك في اقرب وقت."
    }
  }
}
</i18n>
